import {
	RESPONSIBLE_ORGANIZATION_SORT_KEYS,
	type ResponsibleOrganizationListItem,
	type ResponsibleOrganizationSortKey,
	organizationTypeFilterParam,
} from "@seagames/contracts";
import { type ColumnDef, DataTable, ListHeader, ListTag, ListToolbar, PrimaryCell, StatsStrip, useUiStrings } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useRouteContext } from "@tanstack/react-router";
import { PageTabs } from "../../components/common/page-tabs";
import { OrganizationTypesPanel } from "../../components/organization-types/organization-types-panel";
import { tabbedSearch, usePanelSearch } from "../../lib/list-page/tab-search";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { z } from "zod";
import { ConfirmDeleteModal } from "../../components/common/confirm-delete-modal";
import { EditDeleteActions } from "../../components/common/edit-delete-actions";
import { timestampColumn, usageColumn } from "../../components/common/list-columns";
import { type ResponsibleOrganizationFormTarget, ResponsibleOrganizationFormModal } from "../../components/responsible-organizations/responsible-organization-form-modal";
import { countryOptionsOptions } from "../../lib/countries/queries";
import { listSearch } from "../../lib/list-page/list-search";
import { organizationTypeChipOptions, topOrganizationTypeCells } from "../../lib/organization-types/chips";
import { organizationTypeOptionsOptions } from "../../lib/organization-types/queries";
import { useConfirmDelete } from "../../lib/list-page/use-confirm-delete";
import { useListPage } from "../../lib/list-page/use-list-page";
import {
	deleteResponsibleOrganization,
	invalidateResponsibleOrganizations,
	responsibleOrganizationStatsOptions,
	responsibleOrganizationsListOptions,
} from "../../lib/responsible-organizations/queries";

export const Route = createFileRoute("/_app/settings/responsible-organizations")({
	validateSearch: tabbedSearch(["organizations", "types"] as const),
	component: ResponsibleOrganizationsShell,
});

/** The filters as the URL holds them. `""` = not set; the URL drops empties. */
interface ResponsibleOrganizationFilters extends Record<string, unknown> {
	search: string;
	/** A type's uuid, or `none` for organisations with no type. */
	organizationTypeId: string;
	countryId: string;
}
const EMPTY_FILTERS: ResponsibleOrganizationFilters = { search: "", organizationTypeId: "", countryId: "" };

const organizationsSearch = listSearch(RESPONSIBLE_ORGANIZATION_SORT_KEYS, { organization_type_id: organizationTypeFilterParam, country_id: z.string().uuid() });

/** Organizations | Organization types (`?tab=`, absent = organizations); `/settings/organization-types` redirects here. */
function ResponsibleOrganizationsShell() {
	const { t } = useTranslation("nav");
	const tab = Route.useSearch().tab ?? "organizations";
	const navigate = Route.useNavigate();
	return (
		<div className="flex min-w-0 flex-col gap-[1rem]">
			<PageTabs
				label={t("items.responsibleOrganizations")}
				tabs={[
					{ key: "organizations", label: t("items.responsibleOrganizations") },
					{ key: "types", label: t("items.organizationTypes") },
				]}
				current={tab}
				onChange={(next) => void navigate({ search: { tab: next === "organizations" ? undefined : next } })}
			/>
			{tab === "types" ? <OrganizationTypesPanel /> : <ResponsibleOrganizationsPage />}
		</div>
	);
}

function ResponsibleOrganizationsPage() {
	const { t } = useTranslation("responsibleOrganizations");
	const { t: tc } = useTranslation("common");
	const { formatNumber } = useUiStrings();
	const { session } = useRouteContext({ from: "/_app" });
	const { search, navigate } = usePanelSearch(organizationsSearch, "/settings/responsible-organizations");
	const isSuper = session.active_role === "SUPER_ADMIN";
	const [form, setForm] = useState<ResponsibleOrganizationFormTarget | null>(null);

	const page = useListPage({
		search,
		navigate,
		listOptions: (q) => responsibleOrganizationsListOptions({ ...q, organization_type_id: search.organization_type_id, country_id: search.country_id }),
		columnsKey: "responsible-organizations",
		filters: {
			empty: EMPTY_FILTERS,
			fromUrl: (s): ResponsibleOrganizationFilters => ({ search: s.search ?? "", organizationTypeId: s.organization_type_id ?? "", countryId: s.country_id ?? "" }),
			toUrl: (f) => ({ search: f.search.trim() || undefined, organization_type_id: f.organizationTypeId || undefined, country_id: f.countryId || undefined }),
		},
		summary: { range: (v) => t("pagination.summary", v), empty: t("pagination.empty") },
	});
	const countries = useQuery(countryOptionsOptions());
	const types = useQuery(organizationTypeOptionsOptions());
	const stats = useQuery(responsibleOrganizationStatsOptions());
	const del = useConfirmDelete<ResponsibleOrganizationListItem>({
		remove: (r) => deleteResponsibleOrganization(r.responsible_organization_id),
		invalidate: invalidateResponsibleOrganizations,
		deleted: (r) => t("delete.deleted", { name: r.name }),
	});

	// Chip counts come from `/stats` — unfiltered totals, one aggregate statement.
	const typeOptions = organizationTypeChipOptions(types.data, stats.data?.by_organization_type, t("filters.noType"), search.organization_type_id);
	const countryOptions = (countries.data ?? []).map((c) => ({ value: c.country_id, label: c.name }));
	const total = page.list.data?.total;
	const sortValue = `${search.sort ?? "name"}:${search.dir ?? "asc"}`;
	const sortOptions = [
		{ value: "name:asc", label: tc("sort.nameAsc") },
		{ value: "name:desc", label: tc("sort.nameDesc") },
		{ value: "organization_type:asc", label: tc("sort.typeAsc") },
		{ value: "organization_type:desc", label: tc("sort.typeDesc") },
		{ value: "created_at:desc", label: tc("sort.newest") },
		{ value: "created_at:asc", label: tc("sort.oldest") },
	];

	const columns: ColumnDef<ResponsibleOrganizationListItem, ResponsibleOrganizationSortKey>[] = [
		{
			key: "name",
			header: t("table.name"),
			sortable: true,
			lockVisible: true,
			minWidth: "14rem",
			cell: (r) => <PrimaryCell>{r.name}</PrimaryCell>,
		},
		{
			key: "organization_type",
			header: t("table.type"),
			sortable: true,
			// Wide enough for "National Olympic Committee" on one line — the tag never wraps out of its box.
			minWidth: "13rem",
			cell: (r) =>
				r.organization_type ? (
					<ListTag>{r.organization_type.name}</ListTag>
				) : (
					<span className="text-base-content/30">—</span>
				),
		},
		{
			key: "country_name" as ResponsibleOrganizationSortKey,
			header: t("table.country"),
			minWidth: "9rem",
			cell: (r) => (r.country_name ? <span>{r.country_name}</span> : <span className="text-base-content/30">—</span>),
		},
		usageColumn("cards_count", t("table.cards"), t("table.cardsTitle")),
		usageColumn("scopes_count", t("table.scopes"), t("table.scopesTitle"), "7rem"),
		timestampColumn("created_at", t("table.created"), true),
	];

	return (
		<div className="flex min-w-0 flex-col gap-[1.125rem]">
			<ListHeader
				title={t("page.title")}
				count={total === undefined ? undefined : t("page.count", { count: total, n: formatNumber(total) })}
				description={<Trans t={t} i18nKey="page.intro" components={{ strong: <strong className="font-semibold text-gms-ink-2" /> }} />}
				actionLabel={t("page.addOrganization")}
				onAction={isSuper ? () => setForm({ mode: "create" }) : undefined}
			/>

			<StatsStrip
				label={tc("list.summary")}
				cells={[
					{
						key: "total",
						label: t("stats.total"),
						value: total ?? "—",
						note: page.hasFilters ? t("stats.matching") : undefined,
					},
					...topOrganizationTypeCells(stats.data?.by_organization_type),
					...(stats.data ? [{ key: "no-country", label: t("stats.withoutCountry"), value: stats.data.without_country }] : []),
				]}
			/>

			<ListToolbar
				search={search.search ?? ""}
				onSearch={(v) => page.patch({ search: v || undefined })}
				searchPlaceholder={t("filters.searchPlaceholder")}
				sort={{
					value: sortValue,
					options: sortOptions,
					onChange: (v) => {
						const [sort, dir] = v.split(":") as [ResponsibleOrganizationSortKey, "asc" | "desc"];
						page.patch({ sort, dir });
					},
				}}
				chips={[
					{
						kind: "options",
						key: "type",
						label: t("filters.typeChips"),
						options: typeOptions,
						value: search.organization_type_id ? [search.organization_type_id] : [],
						onChange: (v) => page.patch({ organization_type_id: v[0] ?? undefined }),
					},
					{
						kind: "menu",
						key: "country",
						label: t("filters.countryLabel"),
						options: countryOptions,
						value: search.country_id ?? null,
						onChange: (v) => page.patch({ country_id: v ?? undefined }),
						searchPlaceholder: t("filters.searchCountries"),
						disabled: countries.isLoading,
					},
				]}
				onClearAll={page.clearFilters}
			/>

			<DataTable<ResponsibleOrganizationListItem, ResponsibleOrganizationSortKey>
				{...page.table}
				variant="list"
				columns={columns}
				rowKey={(r) => r.responsible_organization_id}
				empty={page.emptyState({
					filtered: { title: t("empty.filteredTitle"), description: t("empty.filteredDescription") },
					none: { title: t("empty.noneTitle"), description: t("empty.noneDescription") },
					clearLabel: t("empty.clearFilters"),
				})}
				rowActions={
					isSuper
						? (r) => (
								<EditDeleteActions
									editLabel={t("table.editLabel", { name: r.name })}
									onEdit={() => setForm({ mode: "edit", organization: r })}
									deleteLabel={t("table.deleteLabel", { name: r.name })}
									onDelete={() => del.ask(r)}
									canDelete={r.cards_count === 0 && r.scopes_count === 0}
									variant="list"
								/>
							)
						: undefined
				}
			/>

			{isSuper && <ResponsibleOrganizationFormModal target={form} onClose={() => setForm(null)} />}
			{isSuper && (
				<ConfirmDeleteModal
					{...del.modal}
					title={t("delete.title")}
					description={<Trans t={t} i18nKey="delete.description" values={{ name: del.row?.name ?? "" }} components={{ strong: <strong className="font-semibold text-base-content" /> }} />}
					confirmLabel={t("delete.confirm")}
					errorPrefix={t("delete.failed")}
				/>
			)}
		</div>
	);
}
